import _ from 'lodash';
import React from 'react';
import Link from 'next/link';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';

import Footer from 'components/Footer';
import MaxWidthContainer from 'components/MaxWidthContainer';
import Menubar from 'components/Menubar';
import MobileTabBar from 'components/MobileTabBar';

import api from 'utils/api';
import { useAppLaunch } from 'contexts/AppLaunch';
import { AGE_GROUPS } from 'utils/ageGroups';
import { SITE } from 'utils/ageProducts';

const PAGE_TITLE =
  '【各年齡層美妝保養指南】18到50+歲護膚及化妝品推薦總覽 | Sorra';
const PAGE_DESC =
  '幾歲開始抗老？不同年齡該用哪種粉底？Sorra 透過十萬筆真實用戶數據，為 18 歲至 55 歲以上的讀者量身打造專屬護膚與化妝品推薦清單。立即查看與你同年齡層的真實美妝評價！';

// 主分類捷徑：護膚品＝alias a（護膚保養）、化妝品＝alias c（底妝）
const findIdByAlias = (firstCategories, alias) => {
  const fc = _.find(
    firstCategories,
    (c) => String(c.alias || '').toLowerCase() === alias
  );
  return fc ? fc.id : null;
};

function AgeGroupBlock({ group, firstCategories, skincareId, makeupId }) {
  const [activeId, setActiveId] = React.useState(null);
  const ageName = group.display || group.name;

  const activeFc = activeId
    ? _.find(firstCategories, (c) => c.id === activeId)
    : null;
  const secondCategories = activeFc ? activeFc.second_categories || [] : [];

  return (
    <section className="border-b border-gray-100 pb-8 last:border-b-0">

      {/* 主分類捷徑按鈕 */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h2 className="text-base font-normal text-[#4A4A4A] lg:text-xl">
          {ageName}
        </h2>
        {skincareId && (
          <Link
            href={`/age/${group.slug}/${skincareId}`}
            prefetch={false}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-sorra-pink px-4 py-2 text-sm font-medium text-sorra-pink transition hover:bg-[#FFF0F2]"
          >
            探索 {ageName} 熱門護膚品 <span aria-hidden>→</span>
          </Link>
        )}
        {makeupId && (
          <Link
            href={`/age/${group.slug}/${makeupId}`}
            prefetch={false}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-sorra-pink px-4 py-2 text-sm font-medium text-sorra-pink transition hover:bg-[#FFF0F2]"
          >
            探索 {ageName} 熱門化妝品 <span aria-hidden>→</span>
          </Link>
        )}
      </div>

      {/* 所有分類（first_category） */}
      <div className="mb-3 text-sm text-[#9B9B9B]">所有分類</div>
      <div className="flex flex-wrap gap-x-5 gap-y-3">
        {firstCategories.map((category) => {
          const isActive = activeId === category.id;
          return (
            <button
              key={category.id}
              type="button"
              onClick={() => setActiveId(isActive ? null : category.id)}
              className={
                'flex items-center gap-x-1 text-[18px] font-normal transition ' +
                (isActive
                  ? 'text-sorra-pink'
                  : 'text-sorra-gray-900 hover:text-sorra-pink')
              }
            >
              <img
                src="/svgs/index-partnership-arrow.svg"
                className={`h-6 w-6 ${isActive ? 'rotate-90 transform' : ''}`}
                alt=""
              />
              <span>{category.name}</span>
            </button>
          );
        })}
      </div>

      {/* 展開：該主分類的細分類，連到 L3 年齡細分類排行頁 */}
      {activeFc && secondCategories.length > 0 && (
        <div className="mt-4 rounded-lg bg-[#F5F5F5] p-4 lg:p-6">
          <p className="text-[16px] leading-8 text-sorra-gray-900">
            {secondCategories.map((sc, idx) => (
              <React.Fragment key={sc.id}>
                <Link
                  prefetch={false}
                  href={`/age/${group.slug}/${activeFc.id}/${sc.id}`}
                  className="underline underline-offset-2 hover:text-sorra-pink"
                >
                  {sc.name}
                </Link>
                {idx < secondCategories.length - 1 && (
                  <span className="px-2 text-sorra-gray-400">|</span>
                )}
              </React.Fragment>
            ))}
          </p>
        </div>
      )}
    </section>
  );
}

export default function AgeGroups({ seo, firstCategories: ssrFirstCategories }) {
  const { notifications, categoryTree } = useAppLaunch();

  // 優先用 SSR 取得的 first_categories（SEO 友善、首屏即有）；
  // SSR 失敗時退回 client 的 category_tree
  const firstCategories =
    ssrFirstCategories && ssrFirstCategories.length > 0
      ? ssrFirstCategories
      : categoryTree || [];

  const skincareId = findIdByAlias(firstCategories, 'a');
  const makeupId = findIdByAlias(firstCategories, 'c');

  return (
    <div>
      <NextSeo
        title={seo.title}
        description={seo.description}
        canonical="https://www.sorra.net/age-groups"
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: '首頁', item: `${SITE}/` },
          { position: 2, name: '各年齡層美妝保養指南', item: `${SITE}/age-groups` },
        ]}
      />
      <main>
        <Menubar props={{ homeExtend: false, sourceUI: 'AgeGroups' }} />
        <div
          className={
            'globals-centerContentsMargin' +
            (notifications.length > 0 ? ' withNoti' : '')
          }
        >
          <MaxWidthContainer className="px-4">
            <div className="flex w-[100%] flex-wrap items-center gap-1 py-[16px] lg:py-[32px]">
              <Link href="/" className="hover:text-sorra-pink" prefetch={false}>
                首頁
              </Link>
              <span className="px-1 text-sorra-gray-400">&gt;</span>
              <span className="">{PAGE_TITLE}</span>
            </div>

            <h1 className="pb-4 text-3xl font-bold text-[#111928] lg:text-4xl">
              專屬你的年齡美妝保養指南
            </h1>
            <p className="pb-8 text-base leading-8 text-[#4A4A4A] lg:pb-12 lg:text-lg">
              {PAGE_DESC}
            </p>

            <div className="flex flex-col gap-y-8 pb-20">
              {AGE_GROUPS.map((group) => (
                <AgeGroupBlock
                  key={group.slug}
                  group={group}
                  firstCategories={firstCategories}
                  skincareId={skincareId}
                  makeupId={makeupId}
                />
              ))}
            </div>
          </MaxWidthContainer>
        </div>

        <Footer />
        <MobileTabBar props={{}} />
      </main>
    </div>
  );
}

export async function getServerSideProps() {
  // first_categories 從 launchData 取得（SEO 友善、首屏即有連結）
  let firstCategories = [];
  try {
    const { data } = await api.get('/api/v2/appLaunch');
    firstCategories = _.get(data, 'category_tree', []) || [];
  } catch (e) {
    // 取 launchData 失敗：交由 client 端的 category_tree 補上
  }

  return {
    props: {
      firstCategories,
      seo: {
        title: PAGE_TITLE,
        description: PAGE_DESC,
      },
    },
  };
}
